import { createSlice } from "@reduxjs/toolkit";
import { 
  fetchAdminUserSubscriptions,
  reactivateAdminUserSubscription,
  suspendAdminUserSubscription
} from "../thunks/subscriptionThunks";

const initialState = {
  items: [],          // table rows
  meta: {             // pagination meta from backend
    page: 1,
    limit: 10,
    totalPages: 1,
    totalCount: 0,
  },
  loading: false,
  error: null,

  // track suspend action state
  actionLoadingId: null,   // which subscription is currently being updated
  actionError: null,

  // optional: keep filters in redux if you want
  filters: {
    search: "",
    status: "",       // "active" | "suspended" | ...
    tier: "",         // "Basic" | "Standard" | "Premium"
    serviceType: "",  // "Maintenance" | "SEO" | "Security" | "Bundle"
  },
};

const userSubscriptionSlice = createSlice({
  name: "userSubscriptions",
  initialState,
  reducers: {
    setUserSubscriptionFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    clearUserSubscriptions: (state) => {
      state.items = [];
      state.meta = initialState.meta;
      state.loading = false;
      state.error = null;
      state.filters = initialState.filters;
      state.actionLoadingId = null;
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH TABLE DATA
      .addCase(fetchAdminUserSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminUserSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.subscriptions || [];
        state.meta = {
          ...state.meta,
          ...(action.payload.meta || {}),
        };
      })
      .addCase(fetchAdminUserSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch user subscriptions";
      })

      // SUSPEND SUBSCRIPTION
      .addCase(suspendAdminUserSubscription.pending, (state, action) => {
        state.actionError = null;
        state.actionLoadingId = action.meta.arg; // subscriptionId passed in
      })
      .addCase(suspendAdminUserSubscription.fulfilled, (state, action) => {
        state.actionLoadingId = null;

        const updatedSub = action.payload; // backend "data"

        // updatedSub likely has `_id` (mongo id)
        const updatedId = updatedSub?._id?.toString?.();

        state.items = state.items.map((sub) => {
          // You might have `_id` or `subscriptionMongoId` depending on your mapping
          const currentId =
            sub.subscriptionMongoId?.toString?.() || sub._id?.toString?.();

          if (updatedId && currentId === updatedId) {
            // Only really care about status & updatedAt here
            return {
              ...sub,
              status: updatedSub.status || "suspended",
              updatedAt: updatedSub.updatedAt || sub.updatedAt,
            };
          }

          return sub;
        });
      })
      .addCase(suspendAdminUserSubscription.rejected, (state, action) => {
        state.actionLoadingId = null;
        state.actionError =
          action.payload?.message || "Failed to suspend subscription";
      })
      .addCase(reactivateAdminUserSubscription.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg;
        state.actionError = null;
      })
      .addCase(reactivateAdminUserSubscription.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.actionLoadingId = null;
      })
      .addCase(reactivateAdminUserSubscription.rejected, (state, action) => {
        state.actionLoadingId = null;
        state.actionError = action.payload;
      });
  },
});

export const {
  setUserSubscriptionFilters,
  clearUserSubscriptions,
} = userSubscriptionSlice.actions;

export default userSubscriptionSlice.reducer;
